import { ThemeWrapper } from "@verity/components";
import { CoreBackButton } from "@verity/components/core";
import { useCallback, useRef, useState } from "react";
import DemoLeadModal, { type LeadFormDraft } from "./DemoLeadModal";
import DemoPreview from "./DemoPreview";
import DemoResult from "./DemoResult";
import { submitDemoLead, type DemoLead } from "./lead-api";

type Screen =
	| { kind: "preview" }
	| { kind: "form"; submitting: boolean; error?: string }
	| { kind: "result"; preparedFor: string };

const SUBMIT_ERROR = "We could not send your details just now. Please check your connection and try again.";

function DemoHero() {
	return (
		<header className="demo-hero">
			<p className="demo-badge">YuJa Verity Test Proctoring Platform</p>
			<h1>Experience Secure Proctoring in Real Time</h1>
			<p className="demo-hero-copy">
				See how Verity helps instructors review activity, flagged events, and the full testing session.
			</p>
		</header>
	);
}

export function DemoApp() {
	const [screen, setScreen] = useState<Screen>({ kind: "preview" });
	const [draft, setDraft] = useState<LeadFormDraft>();
	// State rather than a ref: the modal needs the element on the very render that opens it.
	const [previewEl, setPreviewEl] = useState<HTMLDivElement | null>(null);

	// The form's exit button and Escape stay live while a submission is in flight, so one can settle after
	// the visitor has already left the form. Only the newest submission may move the screen; an abandoned
	// one still reaches the CRM, it just no longer drags the page to a result the visitor navigated away
	// from.
	const latestSubmission = useRef(0);

	const backToPreview = useCallback(() => {
		latestSubmission.current += 1;
		setScreen({ kind: "preview" });
	}, []);

	const submit = async (lead: DemoLead, nextDraft: LeadFormDraft) => {
		const submission = ++latestSubmission.current;
		setDraft(nextDraft);
		setScreen({ kind: "form", submitting: true });
		try {
			await submitDemoLead(lead);
			if (latestSubmission.current === submission) {
				setScreen({ kind: "result", preparedFor: `${lead.firstName} ${lead.lastName}` });
			}
		} catch {
			if (latestSubmission.current === submission) setScreen({ kind: "form", submitting: false, error: SUBMIT_ERROR });
		}
	};

	return (
		<ThemeWrapper scoped={false}>
			<main className="demo-page">
				<DemoHero />
				{screen.kind === "result" ? (
					<>
						<CoreBackButton className="demo-back" text="Back to the preview" onBack={backToPreview} />
						<div className="demo-result">
							<DemoResult preparedFor={screen.preparedFor} />
						</div>
					</>
				) : (
					<DemoPreview rootRef={setPreviewEl} onPreview={() => setScreen({ kind: "form", submitting: false })}>
						{screen.kind === "form" ? (
							<DemoLeadModal
								submitting={screen.submitting}
								error={screen.error}
								draft={draft}
								container={previewEl}
								onClose={backToPreview}
								onSubmit={(lead, nextDraft) => void submit(lead, nextDraft)}
							/>
						) : null}
					</DemoPreview>
				)}
			</main>
		</ThemeWrapper>
	);
}
